import React, { useState, useRef, useEffect } from 'react'
import { QrCode, X, CheckCircle, XCircle } from 'lucide-react'
import QrScanner from 'qr-scanner'

const QrScannerModal = ({ isOpen, onClose, onScan, title = 'QR Kod Okut', description = 'QR kodu kameranın önüne tutun' }) => {
    const [scanResult, setScanResult] = useState(null)
    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState(null) // 'success', 'error', null
    const [message, setMessage] = useState('')
    const videoRef = useRef(null)
    const scannerRef = useRef(null)

    useEffect(() => {
        if (isOpen && videoRef.current && !scanResult) {
            scannerRef.current = new QrScanner(
                videoRef.current,
                (result) => {
                    setScanResult(result.data)
                    // QR okununca handle et
                    handleScan(result.data)
                },
                {
                    returnDetailedScanResult: true,
                    highlightScanRegion: true,
                    highlightCodeOutline: true,
                }
            )

            scannerRef.current.start().catch(err => {
                console.error('Kamera başlatılamadı:', err)
                setStatus('error')
                setMessage('Kameraya erişilemedi. Lütfen izin verin.')
            })
        }

        return () => {
            if (scannerRef.current) {
                scannerRef.current.stop()
                scannerRef.current.destroy()
                scannerRef.current = null
            }
        }
    }, [isOpen, scanResult])

    const handleScan = async (data) => {
        if (scannerRef.current) {
            scannerRef.current.stop()
        }

        setLoading(true)
        try {
            const result = await onScan(data)
            if (result.success) {
                setStatus('success')
                setMessage(result.message || 'İşlem başarılı')
                setTimeout(() => {
                    onClose()
                    // Reset state for next time
                    setScanResult(null)
                    setStatus(null)
                    setMessage('')
                }, 2000)
            } else {
                setStatus('error')
                setMessage(result.message || 'Bir hata oluştu')
            }
        } catch (error) {
            setStatus('error')
            setMessage('İşlem sırasında bir hata oluştu')
        } finally {
            setLoading(false)
        }
    }

    const handleTryAgain = () => {
        setScanResult(null)
        setStatus(null)
        setMessage('')
        if (scannerRef.current) {
            scannerRef.current.start()
        }
    }

    const handleClose = () => {
        setScanResult(null)
        setStatus(null)
        setMessage('')
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className='fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4' onClick={handleClose}>
            <div className='relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl' onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className='p-4 border-b border-gray-100 flex items-center justify-between'>
                    <h3 className='text-lg font-bold text-gray-800 flex items-center gap-2'>
                        <QrCode size={20} className='text-[#AC5E2A]' />
                        {title}
                    </h3>
                    <button onClick={handleClose} className='p-2 hover:bg-gray-100 rounded-full transition-colors'>
                        <X size={20} className='text-gray-400' />
                    </button>
                </div>

                {/* Scanner Area */}
                <div className='relative aspect-square bg-black flex items-center justify-center overflow-hidden'>
                    {!scanResult && <video ref={videoRef} className='w-full h-full object-cover' />}

                    {loading && (
                        <div className='absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white p-6'>
                            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4'></div>
                            <p className='font-medium'>Kontrol ediliyor...</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className='absolute inset-0 bg-green-500/90 flex flex-col items-center justify-center text-white p-6 animate-fadeIn'>
                            <CheckCircle size={64} className='mb-4' />
                            <p className='text-xl font-bold text-center'>{message}</p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className='absolute inset-0 bg-red-500/90 flex flex-col items-center justify-center text-white p-6 animate-fadeIn'>
                            <XCircle size={64} className='mb-4' />
                            <p className='text-xl font-bold text-center mb-6'>{message}</p>
                            <button
                                onClick={handleTryAgain}
                                className='px-8 py-3 bg-white text-red-500 rounded-2xl font-bold active:scale-95 transition-all shadow-lg'
                            >
                                Tekrar Dene
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer Info */}
                <div className='p-6 text-center'>
                    {!status && !loading && (
                        <p className='text-gray-500 text-sm font-medium'>
                            {description}
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default QrScannerModal
